// Sentry for the DMS dashboard.
//
// Imported as the FIRST import in src/index.js so the SDK is installed before any
// application code can throw. No REACT_APP_SENTRY_DSN => complete no-op: nothing is
// initialised and nothing is sent, which is how local dev and any un-provisioned
// build behave.
//
// Note on secrecy: a browser DSN is NOT a secret. It is compiled into the JS bundle
// and visible to anyone with devtools — that is by design, it only grants the ability
// to POST events into this project. That is why it lives in the committed .env
// alongside REACT_APP_BASE_URL rather than in a CI secret.
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;

export const sentryEnabled = Boolean(dsn);

if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    // CI passes the commit sha through as a Docker build arg, so an issue names the
    // build that introduced it.
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

    // Errors only. Tracing and replay are what consume the free plan's quota, and we
    // are starting with "what is broken", not "how fast is it".
    tracesSampleRate: 0,

    // Do not let the SDK record URLs/IPs on its own. Breadcrumbs still capture the
    // user's click path, which is the part that actually helps reproduce a bug.
    sendDefaultPii: false,

    // Browser noise that is not a defect in this app: extension injections, dead
    // networks in-store, and the benign ResizeObserver loop warning that antd's
    // responsive tables trigger.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "Network Error",
      "Failed to fetch",
      "Load failed",
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  });
}

// Called from AuthContext once we know who is signed in, so an issue says which
// user hit it. Id + role only — no email, matching the backend's policy.
export function setSentryUser(user) {
  if (!dsn) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user._id || user.id, role: user.role });
}

export default Sentry;
